import {
    ActionIcon,
    Affix,
    Badge,
    Button,
    Divider,
    Group,
    Paper,
    Stack,
    Text,
    useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconEye, IconKeyboard, IconPlus, IconTrash } from '@tabler/icons-react';
import { EventBus } from 'bpmn-js/lib/BaseViewer';
import Viewer from 'bpmn-js/lib/NavigatedViewer';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import { useActivitiesModelActions } from '../../../../resource/resources/artifacts/activities/activitiesModelResource';
import { useActivitiesActions } from '../../../../resource/resources/artifacts/activities/activitiesResource';
import {
    ContentParentRelation,
    useContentActions,
} from '../../../../resource/resources/artifacts/content/contentResource';
import { TOOLTIPS } from '../../../../resource/tooltipTexts';
import { getLabelAttr } from '../../../pages/bpmn-modeler-demo/bpmn-util/getLabelAttr';
import refExtension from '../../../pages/bpmn-modeler-demo/reference/ref.json';
import { ReferenceRendererModule } from '../../../pages/bpmn-modeler-demo/reference-renderer';
import { CustomRendererModule } from '../../../pages/bpmn-modeler-demo/renderer';
import { ConditionalComponent } from '../../../reusable-components/ConditionalComponent';
import { DisableableIconButtonWithTooltip } from '../../../reusable-components/DisableableIconButtonWithTooltip';
import { LinkCopyButtonComponent } from '../../../reusable-components/LinkCopyButtonComponent';
import { ResourceValidationTitleBar } from '../../../reusable-components/validation/ResourceValidationTitleBar';
import { ActivitiesDrawerContent } from '../../content-drawer/ActivitiesDrawerContent';
import './bpmn-viewer.css';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import ElementRegistry from 'diagram-js/lib/core/ElementRegistry';
import Overlays from 'diagram-js/lib/features/overlays/Overlays';
import { uniq } from 'lodash';
import { Controlled_BooleanInput } from '../../../../../../react-commons/components/inputs/controlled/Controlled_BooleanInput';
import { Controlled_BooleanSwitchInput } from '../../../../../../react-commons/components/inputs/controlled/Controlled_BooleanSwitchInput';
import { OpenModal } from '../../../../../../react-commons/utils/modals/openModal';
import { useContentSectionActions } from '../../../../resource/resources/artifacts/content/contentSectionResource';
import { RoleResource, useRoleActions } from '../../../../resource/resources/artifacts/role/roleResource';
import { BpmnColorPickerModule } from '../../../pages/bpmn-modeler-demo/color-picker';
import { CustomContextPadModule } from '../../../pages/bpmn-modeler-demo/context-pad';
import { CustomPaletteModule } from '../../../pages/bpmn-modeler-demo/palette';
import { ActivitiesReferenceContextPadModule } from '../../../pages/bpmn-modeler-demo/reference/activity-reference/activity-reference-contextpad';
import { ActivitiesReferenceContextPaletteModule } from '../../../pages/bpmn-modeler-demo/reference/activity-reference/activity-reference-palette';
import { ResizeModule } from '../../../pages/bpmn-modeler-demo/resize';
import VersionModule, { VersionService } from '../../../pages/bpmn-modeler-demo/VersionService';
import { LightenDarkenColor } from '../../../util/utilFunctions';
import { RoleDrawerContent } from '../role/RoleDrawerContent';

const BPMNModelerViewerInternal = ({
    bpmnModelerNeeded,
    setBPMNModeler,
}: {
    bpmnModelerNeeded: boolean;
    setBPMNModeler: (modelerInstance: Viewer | null) => void;
}) => {
    const bpmnModelerRef = useRef<Viewer | null>(null);
    const containerRef = React.createRef<HTMLDivElement>();
    const { colorScheme } = useMantineColorScheme();
    const dark = colorScheme === 'dark';

    useEffect(() => {
        if (!containerRef.current || bpmnModelerRef.current) {
            return;
        }

        bpmnModelerRef.current = new Viewer({
            container: containerRef.current,
            bpmnRenderer: {
                defaultFillColor: 'white',
                defaultStrokeColor: 'var(--mantine-color-text)',
            },
            moddleExtensions: {
                pvt: refExtension,
            },
            additionalModules: [CustomRendererModule, ReferenceRendererModule, VersionModule],
            keyboard: {
                bindTo: document,
            },
        });
    }, [containerRef.current]);

    useEffect(() => {
        if (bpmnModelerNeeded && bpmnModelerRef.current) {
            setBPMNModeler(bpmnModelerRef.current);
        }
        return () => {
            const bpmnModeler = bpmnModelerRef.current;
            if (!bpmnModelerNeeded && bpmnModeler) {
                bpmnModelerRef.current = null;
                setBPMNModeler(null);
                bpmnModeler.detach();
                bpmnModeler.destroy();
            }
        };
    }, [bpmnModelerNeeded, bpmnModelerRef.current]);

    return (
        <div style={{ height: '100%' }} className={`modeler-parent ${dark ? 'dark-model' : 'light-model'}`}>
            <div id="modeler-container" style={{ height: '100%' }} ref={containerRef}></div>
        </div>
    );
};

export const BPMNModelerViewer = ({
    activityId,
    onAdd,
    onDelete,
    onEdit,
    link,
    style,
}: {
    activityId: string | undefined;
    onAdd: (() => void) | undefined;
    onDelete: (() => void) | undefined;
    onEdit: (() => void) | undefined;
    link: string | undefined;
    style?: React.CSSProperties;
}) => {
    const { colorScheme } = useMantineColorScheme();
    const dark = colorScheme === 'dark';
    const [bpmnModeler, setBPMNModeler] = useState<Viewer | null>(null);
    const [loadedBpmnModeler, setLoadedBPMNModeler] = useState<Viewer | null>(null);
    const [opened, { open, close }] = useDisclosure(false);
    const [openedRoleDrawerContent, { open: openRoleDrawer, close: closeRoleDrawer }] = useDisclosure(false);

    const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
    const [selectedElementTitle, setSelectedElementTitle] = useState<string | null>(null);
    const [showMultipleRoles, setShowMultipleRoles] = useState<boolean>(false);
    const [activitiesRefObjectId, setActivitiesRefObjectId] = useState<string | undefined>(undefined);

    const [roleFilter, { toggle: toggleRoleFilter }] = useDisclosure(true);
    const [rolesToFilter, setRolesToFilter] = useState<Array<string>>([]);
    const [selectedRole, setSelectedRole] = useState<string | null>(null);
    const [originalColorsRecord, setOriginalColorsRecord] = useState<Record<string, string>>({});

    const activityActions = useActivitiesActions();
    const activityModelActions = useActivitiesModelActions();
    const contentActions = useContentActions();

    const activityQuery = activityActions.useGetResource(activityId);
    const activityModelQuery = activityModelActions.useGetSingleResource(activityId);
    const contentsQuery = contentActions.useGetAllWithSectionResources(
        ContentParentRelation.ACTIVITIES_MODEL,
        activityModelQuery.data?.id
    );
    const rolesQuery = useRoleActions().useGetAllResources(activityModelQuery.data?.versionedIn);

    const relevantRoles = useMemo(() => {
        const rolesList: Array<RoleResource> = [];
        contentsQuery.data?.forEach((value) => {
            value.relevantRoles.forEach((roleId) => {
                const role = rolesQuery.data?.find((value) => value.objectId === roleId);
                if (role) {
                    rolesList.push(role);
                }
            });
        });
        return uniq(rolesList);
    }, [contentsQuery.data, rolesQuery.data]);

    useEffect(() => {
        if (!activityModelQuery.data) {
            return;
        }

        if (bpmnModeler) {
            let bpmnText = activityModelQuery.data.BPMNModel;
            bpmnText = bpmnText.replace('Process_0any3sf', 'Activities');
            bpmnModeler.get<VersionService>('versionService').setVersionId(activityModelQuery.data.versionedIn);
            bpmnModeler.importXML(bpmnText).then(({ warnings }) => {
                if (warnings.length) {
                    console.warn(warnings);
                }

                // @ts-expect-error
                bpmnModeler.get('canvas').zoom('fit-viewport', 'auto');
                setLoadedBPMNModeler(bpmnModeler);
            });
        } else {
            setLoadedBPMNModeler(null);
        }
    }, [activityModelQuery.data, bpmnModeler]);

    useEffect(() => {
        const eventBus: EventBus<any> | undefined = loadedBpmnModeler?.get('eventBus');
        eventBus?.on('element.click', function (event) {
            const element = event.element;
            if (element.id.includes('Activities_Reference')) {
                setActivitiesRefObjectId(element.businessObject.objectId);
            } else {
                setActivitiesRefObjectId(undefined);
            }
            setSelectedElementId(element.id);
            const labelAttr = getLabelAttr(element.businessObject);
            if (labelAttr) {
                const label =
                    labelAttr === 'categoryValueRef' ?
                        element.businessObject.categoryValueRef.value
                    :   element.businessObject[labelAttr];
                setSelectedElementTitle(label);
            } else {
                setSelectedElementTitle(null);
            }
        });
    }, [loadedBpmnModeler]);

    useEffect(() => {
        const modeler = loadedBpmnModeler;
        if (rolesQuery.data && contentsQuery.data && modeler) {
            const overlays: Overlays = modeler.get('overlays');
            const elementRegistry: ElementRegistry = modeler.get('elementRegistry');

            let hasMultipleRoles = false;
            elementRegistry.forEach((element) => {
                if (
                    element.type !== 'label' &&
                    element.type !== 'bpmn:Process' &&
                    element.type !== 'bpmn:SequenceFlow'
                ) {
                    const content = contentsQuery.data?.find((value) => value.modelElement === element.id);
                    if (content) {
                        if (content.hasContentSections) {
                            overlays.add(element.id, {
                                position: {
                                    top: 0,
                                    right: -4,
                                },
                                html: `<span class="custom-badge">C</span>`,
                            });
                        }
                        const gfx = elementRegistry.getGraphics(element);
                        const visual = gfx.querySelector('.djs-visual');
                        const shape = visual?.firstElementChild;
                        if (roleFilter && relevantRoles.length > 0) {
                            const filteredRoles = content.relevantRoles.filter((roleId) =>
                                rolesToFilter.includes(roleId)
                            );
                            const roles =
                                rolesQuery.data?.filter((value) => filteredRoles.includes(value.objectId)) ?? [];
                            const color =
                                roles.length > 1 ? '#215967'
                                : roles.length === 1 ? roles[0].color
                                : 'gray';
                            if (roles.length > 1) {
                                hasMultipleRoles = true;
                            }

                            if (shape) {
                                const styleAttribute = shape.getAttribute('style');
                                if (styleAttribute) {
                                    if (!(element.id in originalColorsRecord)) {
                                        const record = originalColorsRecord;
                                        record[element.id] = styleAttribute;
                                        setOriginalColorsRecord(record);
                                    }
                                    shape.setAttribute(
                                        'style',
                                        styleAttribute
                                            .replace(
                                                /fill:[^;]+;/,
                                                `fill: ${LightenDarkenColor(color, roles.length > 1 ? 75 : 150)};`
                                            )
                                            .replace(/stroke:[^;]+;/, `stroke: ${color};`)
                                    );
                                }
                                shape.setAttribute('fill', LightenDarkenColor(color, roles.length > 1 ? 75 : 150));
                                shape.setAttribute('stroke', color);
                            }
                        } else {
                            const originalColor = originalColorsRecord[element.id];
                            if (originalColor && shape) {
                                shape.setAttribute('style', originalColor);
                            }
                        }
                    }
                }
            });
            setShowMultipleRoles(hasMultipleRoles);
        }
    }, [contentsQuery.data, rolesQuery.data, loadedBpmnModeler, roleFilter, rolesToFilter]);

    useEffect(() => {
        if (
            selectedElementId &&
            (contentsQuery.data?.some((value) => value.modelElement === selectedElementId) ||
                activitiesRefObjectId !== undefined)
        ) {
            open();
            closeRoleDrawer();
        }
    }, [selectedElementId]);

    useEffect(() => {
        if (contentsQuery.data) {
            const rolesToUpdate: Array<string> = [];
            contentsQuery.data.forEach((content) => {
                rolesToUpdate.push(...content.relevantRoles);
            });
            setRolesToFilter(uniq(rolesToUpdate));
        }
    }, [contentsQuery.data]);

    return (
        <div
            style={
                style ?? {
                    position: 'absolute',
                    textAlign: 'center',
                    width: 'calc(100vw - 220px)',
                    left: '220px',
                    padding: '10px',
                    height: '100%',
                }
            }
            id={'bpmn-viewer'}
        >
            <ResourceValidationTitleBar
                customButtons={
                    <Group>
                        <Show when={link}>
                            <LinkCopyButtonComponent link={link!} section={'activities'} />
                        </Show>
                        {!activityId && onAdd && (
                            <DisableableIconButtonWithTooltip
                                disabled={false}
                                tooltipText={TOOLTIPS.CREATE_NEW_REFERENCE}
                                icon={<IconPlus />}
                                onClick={onAdd}
                            />
                        )}
                        {activityId && onDelete && (
                            <DisableableIconButtonWithTooltip
                                disabled={false}
                                tooltipText={TOOLTIPS.DELETE}
                                icon={<IconTrash />}
                                onClick={onDelete}
                            />
                        )}
                        {activityId && onEdit && (
                            <DisableableIconButtonWithTooltip
                                disabled={false}
                                tooltipText={TOOLTIPS.EDIT}
                                icon={<IconEdit />}
                                onClick={onEdit}
                            />
                        )}
                    </Group>
                }
            >
                <Text c={'dimmed'}>Activity : {activityQuery.data?.name ?? 'No Association'}</Text>
            </ResourceValidationTitleBar>
            <ConditionalComponent label={'No activity association found'} condition={Boolean(activityId)}>
                <div
                    style={{ height: 'calc(100% - 77px)' }}
                    className={`modeler-parent ${dark ? 'dark-model' : 'light-model'}`}
                >
                    <ActivitiesDrawerContent
                        openedDrawer={opened}
                        closeDrawer={() => {
                            setSelectedElementId(null);
                            setSelectedElementTitle(null);
                            close();
                        }}
                        activitiesRefObjectId={activitiesRefObjectId}
                        activitiesModelId={activityModelQuery.data?.id ?? ''}
                        elementId={selectedElementId ?? ''}
                        elementTitle={selectedElementTitle}
                        contentResource={
                            contentsQuery.data?.find((value) => value.modelElement === selectedElementId) ?? null
                        }
                        readOnly={true}
                    />
                    <RoleDrawerContent
                        closeDrawer={() => {
                            setSelectedRole(null);
                            closeRoleDrawer();
                        }}
                        openedDrawer={openedRoleDrawerContent}
                        roleId={selectedRole ?? ''}
                    />
                    <BPMNModelerViewerInternal
                        setBPMNModeler={setBPMNModeler}
                        bpmnModelerNeeded={bpmnModeler === null}
                    />
                    <Show when={!opened && !openedRoleDrawerContent}>
                        <Affix position={{ top: 90, right: 15 }}>
                            <ActionIcon
                                onClick={() => {
                                    OpenModal.ContentModal({
                                        title: 'Keyboard Shortcuts',
                                        content: (
                                            <Group w={350}>
                                                <Stack>
                                                    <Text>Zoom</Text>
                                                    <Text>Scrolling (Horizontal)</Text>
                                                </Stack>
                                                <div style={{ flexGrow: 1 }}></div>
                                                <Stack align="flex-end">
                                                    <Text>ctrl + Scrolling</Text>
                                                    <Text>⇧ + Scrolling</Text>
                                                </Stack>
                                            </Group>
                                        ),
                                    });
                                }}
                                variant={'default'}
                                color={'var(--mantine-color-base-button)'}
                            >
                                <IconKeyboard />
                            </ActionIcon>
                        </Affix>
                        {relevantRoles.length > 0 && (
                            <Affix position={{ top: 130, right: 15 }}>
                                <Paper mah={400} withBorder={true} p={'sm'} shadow="lg" style={{ overflowY: 'auto' }}>
                                    <Stack>
                                        <Group>
                                            <Controlled_BooleanSwitchInput
                                                value={roleFilter}
                                                onChange={toggleRoleFilter}
                                                readOnly={false}
                                            />
                                            <Text w={'100%'} style={{ textAlign: 'center' }}>
                                                Relevant Roles
                                            </Text>
                                        </Group>
                                        <Divider />
                                        <Controlled_BooleanInput
                                            nullable={false}
                                            value={relevantRoles.length === rolesToFilter.length}
                                            onChange={() => {
                                                if (relevantRoles.length === rolesToFilter.length) {
                                                    setRolesToFilter([]);
                                                } else {
                                                    setRolesToFilter(relevantRoles.map((value) => value.objectId));
                                                }
                                            }}
                                            readOnly={false}
                                            labelPosition={'right'}
                                            label={'Select All'}
                                        />
                                        {relevantRoles.map((role) => (
                                            <Group key={role.objectId}>
                                                <Controlled_BooleanInput
                                                    nullable={false}
                                                    value={rolesToFilter.includes(role.objectId)}
                                                    onChange={() => {
                                                        if (rolesToFilter.includes(role.objectId)) {
                                                            setRolesToFilter(
                                                                rolesToFilter.filter((value) => value !== role.objectId)
                                                            );
                                                        } else {
                                                            setRolesToFilter([...rolesToFilter, role.objectId]);
                                                        }
                                                    }}
                                                    readOnly={false}
                                                />
                                                <Badge
                                                    variant={'filled'}
                                                    color={LightenDarkenColor(role.color, 150)}
                                                    style={{ border: 2, borderStyle: 'solid', borderColor: role.color }}
                                                    fullWidth
                                                    autoContrast
                                                >
                                                    {role.name}
                                                </Badge>
                                                <ActionIcon
                                                    size={'sm'}
                                                    onClick={() => {
                                                        setSelectedRole(role.id);
                                                        close();
                                                        openRoleDrawer();
                                                    }}
                                                    variant={'transparent'}
                                                    color={'var(--mantine-color-base-button)'}
                                                    disabled={false}
                                                >
                                                    <IconEye />
                                                </ActionIcon>
                                            </Group>
                                        ))}
                                        <Show when={showMultipleRoles}>
                                            <Divider />
                                            <Badge
                                                variant={'filled'}
                                                color={LightenDarkenColor('#215967', 75)}
                                                style={{
                                                    border: 2,
                                                    borderStyle: 'solid',
                                                    borderColor: '#215967',
                                                }}
                                                fullWidth
                                                autoContrast
                                            >
                                                Multiple Roles
                                            </Badge>
                                        </Show>
                                    </Stack>
                                </Paper>
                            </Affix>
                        )}
                    </Show>
                </div>
            </ConditionalComponent>
        </div>
    );
};
